import { useState } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button, Dialog, Select, Switch } from "@akuna/ui";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";
import {
  useDaycareHotelSyncSettings,
  useEnableDaycareHotelSync,
  useDisableDaycareHotelSync,
} from "@/hooks/useDaycareHotelSyncSettings";
import { SectionCard } from "../accommodations/primitives";

export default function DaycareHotelSyncToggle({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation("daycareHotel");
  const { data: customerId = null } = useCurrentClinicCustomerId();
  const { data: syncSettings } = useDaycareHotelSyncSettings();
  const enableSync = useEnableDaycareHotelSync();
  const disableSync = useDisableDaycareHotelSync();

  const [isChoosingSource, setIsChoosingSource] = useState(false);
  const [isConfirmingDisable, setIsConfirmingDisable] = useState(false);
  const [sourceClinicId, setSourceClinicId] = useState("");

  const { data: clinics = [], isLoading: isLoadingClinics } = useQuery({
    queryKey: ["clinics-for-sync-toggle", customerId],
    queryFn: async () => {
      if (!customerId) return [];
      const { data, error } = await supabase
        .from("clinics")
        .select("id, name")
        .eq("customer_id", customerId);
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
    enabled: !!customerId,
  });

  const isEnabled = syncSettings?.isEnabled ?? false;

  // Só faz sentido oferecer sincronização quando o customer tem mais de uma
  // clínica — mas nunca esconde um switch já ligado, nem some durante o load.
  if (!isEnabled && !isLoadingClinics && clinics.length < 2) return null;

  const handleToggle = (checked: boolean) => {
    if (checked) {
      setSourceClinicId("");
      setIsChoosingSource(true);
    } else {
      setIsConfirmingDisable(true);
    }
  };

  const confirmEnable = async () => {
    if (!sourceClinicId) return;
    try {
      await enableSync.mutateAsync({ sourceClinicId });
      toast({ title: t("settings.sync.toastEnabled") });
      setIsChoosingSource(false);
    } catch {
      toast({ variant: "destructive", title: t("settings.sync.toastError") });
    }
  };

  const confirmDisable = async () => {
    try {
      await disableSync.mutateAsync();
      toast({ title: t("settings.sync.toastDisabled") });
      setIsConfirmingDisable(false);
    } catch {
      toast({ variant: "destructive", title: t("settings.sync.toastError") });
    }
  };

  return (
    <section>
      <SectionCard className="flex items-center justify-between gap-4 p-4">
        <div>
          <p className="font-display text-sm font-bold text-foreground">{t("settings.sync.title")}</p>
          <p className="text-xs text-color-light">{t("settings.sync.description")}</p>
        </div>
        <Switch checked={isEnabled} onCheckedChange={handleToggle} label={t("settings.sync.title")} disabled={!canEdit} />
      </SectionCard>

      <Dialog
        className="akuna-dialog--compact"
        closeLabel={t("common.cancel")}
        icon={
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-50 text-akuna-orange">
            <RefreshCw aria-hidden="true" size={22} />
          </span>
        }
        header={<span>{t("settings.sync.title")}</span>}
        open={isChoosingSource}
        onOpenChange={setIsChoosingSource}
        title={t("settings.sync.dialogTitle")}
        mode="adaptive"
        footer={
          <div className="flex w-full items-center justify-between gap-3">
            <Button type="button" variant="outline" onClick={() => setIsChoosingSource(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              onClick={confirmEnable}
              disabled={!sourceClinicId || enableSync.isPending}
            >
              {t("settings.sync.confirmEnable")}
            </Button>
          </div>
        }
      >
        <div className="grid gap-3">
          <p className="text-sm text-muted-foreground">{t("settings.sync.dialogDescription")}</p>
          <Select
            label={t("settings.sync.sourceClinicLabel")}
            placeholder={t("settings.sync.sourceClinicPlaceholder")}
            value={sourceClinicId}
            onValueChange={setSourceClinicId}
            options={clinics.map((clinic) => ({ label: clinic.name, value: clinic.id }))}
          />
        </div>
      </Dialog>

      <Dialog
        className="akuna-dialog--compact"
        closeLabel={t("common.cancel")}
        header={<span>{t("settings.sync.title")}</span>}
        open={isConfirmingDisable}
        onOpenChange={setIsConfirmingDisable}
        title={t("settings.sync.disableConfirmTitle")}
        mode="adaptive"
        footer={
          <div className="flex w-full items-center justify-between gap-3">
            <Button type="button" variant="outline" onClick={() => setIsConfirmingDisable(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              data-testid="confirm-disable-sync"
              onClick={confirmDisable}
              disabled={disableSync.isPending}
            >
              {t("settings.sync.confirmDisable")}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-muted-foreground">{t("settings.sync.disableConfirmDescription")}</p>
      </Dialog>
    </section>
  );
}
